<script setup lang="ts">
import { faIconStyle } from '~/utils/faIconStyle'
import { useHome } from '~/composables/useHome'
import StarRating from '~/components/ui/StarRating.vue'

const { getReviews } = useHome()
const { data: years } = await useAsyncData('home-reviews-years', () => getReviews())

const sortedYears = computed(() => {
    if (!years.value) return []
    return [...years.value].sort((a, b) => b.year - a.year)
})

const activeYear = ref<string | null>(null)

// Initialize active year with the most recent one
onMounted(() => {
    if (sortedYears.value?.length) {
        activeYear.value = sortedYears.value[0]?.id || null
    }
})

const selectedYearData = computed(() => {
    if (!activeYear.value) return sortedYears.value[0] || null
    return sortedYears.value.find(y => y.id === activeYear.value) || sortedYears.value[0] || null
})

// Format year as "2024-2025" if it's 2025
const formatSchoolYear = (year: number) => {
    return `${year - 1}-${year}`
}
const getSafeRating = (value: number, max?: number) => {
    if (isNaN(value)) return 0
    // Use provided max, otherwise fallback to 5
    const safeMax = (max && max > 0) ? max : 5
    return (value / safeMax) * 5
}
</script>

<template>
  <section class="py-section-py bg-background relative overflow-hidden transition-colors duration-300">
    <div class="container mx-auto px-container-px relative z-10">
      
      <!-- Unified Section Header -->
      <UiSectionHeader 
        titlePrefix="NOS ÉTUDIANT·E·S"
        titleHighlight="NOUS ÉVALUENT"
        subtitle="La réussite et la satisfaction de nos étudiants sont nos priorités absolues."
      />

      <!-- Year Filter - Using standard UiTabs -->
      <div v-if="sortedYears.length > 1" class="flex flex-col items-center justify-center mb-20 animate-fade-in-up" style="animation-delay: 0.3s">
        
        <!-- Mobile Mobile Select -->
        <UiMobileSelect
          :model-value="`Année ${formatSchoolYear(sortedYears.find(y => y.id === activeYear)?.year ?? sortedYears[0]?.year ?? 0)}`"
          @update:model-value="(label: string) => activeYear = sortedYears.find(y => `Année ${formatSchoolYear(y.year)}` === label)?.id ?? sortedYears[0]?.id ?? null"
          :options="sortedYears.map(y => `Année ${formatSchoolYear(y.year)}`)"
          label="Filtrer par année"
          class="md:hidden w-full max-w-xs"
        />

        <!-- Desktop Tabs -->
        <div class="hidden md:block">
            <UiTabs 
            :items="sortedYears.map(y => `Année ${formatSchoolYear(y.year)}`)" 
            :model-value="`Année ${formatSchoolYear(sortedYears.find(y => y.id === activeYear)?.year ?? sortedYears[0]?.year ?? 0)}`"
            @update:model-value="(label: string) => activeYear = sortedYears.find(y => `Année ${formatSchoolYear(y.year)}` === label)?.id ?? sortedYears[0]?.id ?? null"
            >
            <template #item="{ item }">
                <span class="flex flex-col items-center leading-tight">
                <span class="mb-0.5">{{ item.split(' ')[0] }}</span>
                <span class="flex flex-col items-center">
                    <span>{{ item.split(' ')[1]?.split('-')[0] }}</span>
                    <span class="text-xs opacity-50 my-[-2px]">-</span>
                    <span>{{ item.split(' ')[1]?.split('-')[1] }}</span>
                </span>
                </span>
            </template>
            </UiTabs>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="!selectedYearData" class="text-center py-20 text-foreground-dim">
          Aucune donnée disponible pour le moment.
      </div>

      <!-- Review Content (Themed Layout) -->
      <div v-else class="max-w-6xl mx-auto animate-fade-in-up" style="animation-delay: 0.5s">
          
          <!-- Promotion Header & Global Satisfaction -->
          <div class="flex flex-col md:flex-row justify-between items-center md:items-center mb-section-header-mb gap-10 text-center md:text-left">
              <div class="space-y-2">
                <span class="text-oxo-red font-mono text-[10px] md:text-sm uppercase tracking-[0.2em] font-black">Questionnaire de satisfaction</span>
                <h3 class="text-4xl md:text-6xl font-display font-black text-foreground leading-tight">
                  Année {{ formatSchoolYear(selectedYearData.year) }}
                </h3>
              </div>

              <div class="flex flex-col items-center md:items-end">
                  <div class="text-oxo-red mb-4 transform scale-[1.5] animate-bounce-subtle">
                      <i class="fas fa-thumbs-up" aria-hidden="true"></i>
                  </div>
                  <div class="flex flex-col items-center md:items-end">
                      <StarRating 
                        :rating="getSafeRating(selectedYearData.globalSatisfaction, 100)" 
                        :show-value="true" 
                        size="lg"
                        color="text-oxo-red"
                        text-color="text-foreground"
                        :max-rating="5"
                      />
                      <span class="text-xs font-black text-foreground-dim mt-3 uppercase tracking-[0.2em]">Satisfaction générale</span>
                  </div>
              </div>
          </div>

          <!-- Criteria Grid (6 columns) -->
          <div class="flex flex-wrap justify-center gap-y-16 gap-x-8">
              <div 
                v-for="stat in selectedYearData.stats?.sort((a,b) => a.orderIndex - b.orderIndex)" 
                :key="stat.id" 
                class="flex flex-col items-center text-center group grow basis-[160px] max-w-[200px]"
              >
                  <!-- Icon Container -->
                  <div class="w-20 h-20 mb-6 bg-white/5 rounded-2xl border border-white/5 flex items-center justify-center transition-all duration-500 group-hover:border-oxo-red/30 group-hover:bg-oxo-red/5 group-hover:-translate-y-2">
                      <i :style="faIconStyle('fas fa-' + (stat.icon || 'star'))" :class="'fas fa-' + (stat.icon || 'star') + ' text-3xl text-oxo-red group-hover:animate-pulse-slow'" aria-hidden="true"></i>
                  </div>

                  <!-- Stars -->
                  <div class="mb-4">
                      <StarRating 
                        :rating="getSafeRating(stat.value, stat.maxValue)" 
                        size="sm"
                        color="text-oxo-red"
                      />
                  </div>

                  <!-- Score -->
                  <div class="mb-3">
                      <span class="text-2xl font-display font-black text-foreground">
                          {{ getSafeRating(stat.value, stat.maxValue).toFixed(1) }} <span class="text-foreground-dim text-xs font-bold ml-1">/ 5</span>
                      </span>
                  </div>

                  <!-- Label -->
                  <p class="text-[10px] font-black uppercase tracking-widest text-foreground-dim group-hover:text-foreground transition-colors duration-300 leading-relaxed max-w-[120px]">
                      {{ stat.label }}
                  </p>
              </div>
          </div>

          <!-- Legend Footer -->
          <div class="mt-24 pt-10 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-6">
              <p class="text-[11px] text-foreground-dim uppercase tracking-widest italic font-medium">
                Indice de satisfaction recueilli après chaque cours, anonymisé, et mis à jour chaque année.
              </p>
              <div class="flex items-center gap-3">
                <div class="w-2 h-2 rounded-full bg-oxo-red animate-pulse"></div>
                <span class="text-[10px] font-black text-foreground uppercase tracking-widest">Données certifiées</span>
              </div>
          </div>
      </div>
     
    </div>
  </section>
</template>

<style scoped>
.animate-bounce-subtle {
    animation: bounce 3s infinite;
}

@keyframes bounce {
    0%, 100% { transform: translateY(0) scale(1.5); }
    50% { transform: translateY(-5px) scale(1.5); }
}

.animate-pulse-slow {
    animation: pulse 4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
}
</style>
